import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Phone, PhoneOff, Loader2, Mic, MicOff, Volume2, Send, Sparkles, History, AlertTriangle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const ACCENT_LABEL = {
  us_english: 'US English', indian_english: 'Indian English', hinglish: 'Hinglish',
  british_english: 'British English', australian_english: 'Australian English',
};
const SAMPLE_PROMPTS = ['Ask me about your pricing', 'What do you sell?', 'Where are you based?'];

const AgentTesterInner = () => {
  const [starting, setStarting] = useState(false);
  const [meta, setMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [transcripts, setTranscripts] = useState([]);
  const [kbGaps, setKbGaps] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const endedByUser = useRef(false);
  const everConnected = useRef(false);
  const messagesRef = useRef([]);
  const metaRef = useRef(null);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/api/knowledge/voice-agent/transcripts');
      setTranscripts(data.transcripts || []);
      setKbGaps(data.kb_gaps || []);
    } catch (e) { /* non-blocking */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const saveTranscript = useCallback(async () => {
    const msgs = messagesRef.current;
    if (!msgs || msgs.length === 0) return;
    try {
      await api.post('/api/knowledge/voice-agent/transcripts', {
        messages: msgs, accent: metaRef.current?.accent, market_label: metaRef.current?.market,
      });
      loadHistory();
    } catch (e) { /* non-blocking */ }
  }, [loadHistory]);

  const conversation = useConversation({
    onConnect: () => { everConnected.current = true; toast.success('Connected — talk or type'); },
    onDisconnect: (details) => {
      console.warn('[agent-test] disconnected', details);
      if (everConnected.current) saveTranscript();
      if (!endedByUser.current && everConnected.current) {
        toast.info('Voice session ended. If it dropped right away, allow mic & speaker access and try again.');
      }
      endedByUser.current = false;
      everConnected.current = false;
    },
    onError: (e) => { console.error('[agent-test]', e); toast.error('Session error — please try again'); },
    onMessage: (p) => {
      if (!p?.message) return;
      setMessages((m) => {
        const nx = [...m, { source: p.source === 'ai' ? 'ai' : 'user', text: p.message }];
        messagesRef.current = nx;
        return nx;
      });
    },
  });

  const status = conversation.status;
  const isSpeaking = conversation.isSpeaking;
  const micMuted = conversation.micMuted;
  const live = status === 'connected';

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, live]);

  const start = useCallback(async () => {
    setStarting(true);
    setMessages([]); messagesRef.current = [];
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const { data } = await api.post('/api/knowledge/voice-agent/test-session');
      setMeta({ accent: data.accent, market: data.market_label });
      metaRef.current = { accent: data.accent, market: data.market_label };
      if (data.credit_charged) {
        toast.info(`~${data.credit_cost_credits} credits used for this test session`);
      }
      conversation.startSession({
        conversationToken: data.conversation_token,
        connectionType: 'webrtc',
        dynamicVariables: data.dynamic_variables || {},
      });
    } catch (e) {
      if (e?.name === 'NotAllowedError' || e?.name === 'NotFoundError') toast.error('Microphone permission is required to talk to your agent');
      else toast.error(e?.response?.data?.detail || 'Could not start the session');
    } finally { setStarting(false); }
  }, [conversation]);

  const stop = useCallback(() => {
    endedByUser.current = true;
    try { conversation.endSession(); } catch (e) { /* noop */ }
  }, [conversation]);

  const sendText = useCallback((text) => {
    const t = (text || '').trim();
    if (!t || !live) return;
    try { conversation.sendUserMessage(t); } catch (e) { /* noop */ }
    setMessages((m) => { const nx = [...m, { source: 'user', text: t }]; messagesRef.current = nx; return nx; });
    setInput('');
  }, [conversation, live]);

  const toggleMute = useCallback(() => { try { conversation.setMuted(!micMuted); } catch (e) { /* noop */ } }, [conversation, micMuted]);

  return (
    <Card data-testid="agent-tester-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Volume2 className="h-4 w-4 text-primary" /> Test your Agent — talk or type
        </CardTitle>
        <CardDescription>
          Have a live conversation with your AI agent — by voice or text. It answers from your
          Knowledge Base in a voice tuned to your market. Uses ~5 credits per test session.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div ref={scrollRef} className="h-56 overflow-y-auto rounded-lg border border-border bg-muted/20 p-3 space-y-2" data-testid="agent-tester-transcript">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
              <div className={`relative flex items-center justify-center h-16 w-16 rounded-full ${live ? 'bg-emerald-500/15' : 'bg-muted'}`}>
                {live && <span className="absolute inset-0 rounded-full animate-pulse bg-emerald-500/10" />}
                {live ? <Mic className="h-6 w-6 text-emerald-500" /> : <Phone className="h-6 w-6" />}
              </div>
              <p className="text-sm">{live ? 'Listening — start talking, or type below.' : 'Press “Start” below, allow your mic, then talk or type.'}</p>
              <p className="text-xs flex items-center gap-1"><Sparkles className="h-3 w-3" /> Try: “Ask me about your pricing”</p>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.source === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`agent-msg-${m.source}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.source === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-card border border-border rounded-bl-sm'}`}>{m.text}</div>
              </div>
            ))
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground mr-1">Try:</span>
          {SAMPLE_PROMPTS.map((p) => (
            <button key={p} type="button" onClick={() => sendText(p)} disabled={!live}
              className="text-xs rounded-full border border-border px-2.5 py-1 hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid={`agent-sample-${p.split(' ')[1] || 'prompt'}`}>{p}</button>
          ))}
        </div>

        <div className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') sendText(input); }}
            placeholder={live ? 'Type a message…' : 'Start the session to chat'} disabled={!live} data-testid="agent-text-input" />
          <Button onClick={() => sendText(input)} disabled={!live || !input.trim()} data-testid="agent-send-btn"><Send className="h-4 w-4" /></Button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm" data-testid="agent-tester-status">
            {(status === 'connecting' || starting) ? <span className="text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />Connecting…</span>
              : live ? <span className="font-medium flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />{isSpeaking ? 'Agent speaking…' : 'Live'}</span>
              : <span className="text-muted-foreground flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-muted-foreground/40" />Ready — press Start to talk or type</span>}
            {meta && <Badge variant="outline" className="text-xs" data-testid="agent-accent">{meta.market || 'your market'} · {ACCENT_LABEL[meta.accent] || meta.accent}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {live && <Button variant="outline" size="icon" onClick={toggleMute} data-testid="agent-mic-toggle" title={micMuted ? 'Unmute mic' : 'Mute mic'}>{micMuted ? <MicOff className="h-4 w-4 text-muted-foreground" /> : <Mic className="h-4 w-4 text-emerald-500" />}</Button>}
            {!live ? (
              <Button onClick={start} disabled={starting} data-testid="agent-start-btn">{starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}Start</Button>
            ) : (
              <Button variant="destructive" onClick={stop} data-testid="agent-end-btn"><PhoneOff className="h-4 w-4 mr-2" /> End</Button>
            )}
          </div>
        </div>

        {/* KB gaps to fill */}
        {kbGaps.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3" data-testid="agent-kb-gaps">
            <p className="text-xs font-semibold text-amber-500 flex items-center gap-1.5 mb-2"><AlertTriangle className="h-3.5 w-3.5" /> Knowledge gaps to fill — the agent couldn't answer these:</p>
            <div className="flex flex-wrap gap-1.5">
              {kbGaps.map((g, i) => <span key={i} className="text-xs rounded-md bg-background border border-amber-500/30 px-2 py-1" data-testid="agent-kb-gap-item">{g}</span>)}
            </div>
          </div>
        )}

        {/* Recent test conversations */}
        {transcripts.length > 0 && (
          <div>
            <button type="button" onClick={() => setShowHistory((v) => !v)} className="text-xs text-muted-foreground flex items-center gap-1.5 hover:text-foreground" data-testid="agent-history-toggle">
              <History className="h-3.5 w-3.5" /> Recent tests ({transcripts.length})
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            </button>
            {showHistory && (
              <div className="mt-2 space-y-1.5 max-h-52 overflow-y-auto" data-testid="agent-history-list">
                {transcripts.map((t) => (
                  <div key={t.id} className="rounded-md border border-border p-2 text-xs" data-testid="agent-history-item">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{new Date(t.created_at).toLocaleString()}</span>
                      <span className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">{t.message_count} msgs</Badge>
                        {t.gap_count > 0 && <Badge className="text-[10px] bg-amber-500/20 text-amber-600 border-amber-500/30">{t.gap_count} gap{t.gap_count > 1 ? 's' : ''}</Badge>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const VoiceAgentTester = () => (
  <ConversationProvider>
    <AgentTesterInner />
  </ConversationProvider>
);

export default VoiceAgentTester;
